import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Bell, 
  Check, 
  CheckCircle2, 
  XCircle, 
  Edit, 
  Star, 
  Gift, 
  MessageCircle, 
  Image, 
  Award,
  DollarSign,
  Send,
  X
} from 'lucide-react';

const iconMap = {
  'bell': Bell,
  'check': Check,
  'check-circle': CheckCircle2,
  'x-circle': XCircle,
  'x': X,
  'edit': Edit,
  'star': Star,
  'gift': Gift,
  'message-circle': MessageCircle,
  'image': Image,
  'award': Award,
  'dollar-sign': DollarSign,
  'send': Send,
};

const typeColors = {
  'REQUEST_APPROVED': 'bg-emerald-100 text-emerald-600',
  'REQUEST_REJECTED': 'bg-red-100 text-red-600',
  'REQUEST_MODIFIED': 'bg-orange-100 text-orange-600',
  'MODIFICATION_ACCEPTED': 'bg-emerald-100 text-emerald-600',
  'MODIFICATION_REJECTED': 'bg-red-100 text-red-600',
  'BADGE_EARNED': 'bg-yellow-100 text-yellow-600',
  'REDEMPTION_STATUS_CHANGED': 'bg-purple-100 text-purple-600',
  'NEW_COMMUNICATION': 'bg-blue-100 text-blue-600',
  'CREDENTIAL_APPROVED': 'bg-emerald-100 text-emerald-600',
  'CREDENTIAL_REJECTED': 'bg-red-100 text-red-600',
  'ARTWORK_APPROVED': 'bg-emerald-100 text-emerald-600',
  'ARTWORK_REJECTED': 'bg-amber-100 text-amber-600',
};

export default function NotificationsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch unread count periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await notificationsAPI.getCount();
      setUnreadCount(res.data.unread);
    } catch (error) {
      console.log('Error fetching notification count');
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await notificationsAPI.getAll(20);
      setNotifications(res.data);
    } catch (error) {
      console.log('Error fetching notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await notificationsAPI.markAsRead(notification.id);
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.log('Error marking notification as read');
      }
    }
    
    // Navigate if link_url exists
    if (notification.link_url) {
      setIsOpen(false);
      navigate(notification.link_url);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.log('Error marking all as read');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString('es-EC', { day: 'numeric', month: 'short' });
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Bell;
    return IconComponent;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={handleToggle}
        data-testid="notifications-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden"
             data-testid="notifications-dropdown">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <h3 className="font-semibold text-slate-900">Notificaciones</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                data-testid="mark-all-read-btn"
              >
                Marcar todo como leído
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="py-8 text-center text-slate-500">
                <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Cargando...
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Bell className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No hay notificaciones</p>
                <p className="text-sm">Te avisaremos cuando haya novedades</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const IconComponent = getIcon(notification.icon);
                const colorClass = typeColors[notification.notification_type] || 'bg-slate-100 text-slate-600';
                
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${
                      !notification.is_read ? 'bg-blue-50/50' : ''
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notification.is_read ? 'font-semibold' : 'font-medium'} text-slate-900`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2 mt-0.5">
                          {notification.message}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
              >
                Ver todas las notificaciones
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
